import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import EmptyState from "../../../components/shared/EmptyState";
import LoadingState from "../../../components/shared/LoadingState";

// ── /admin/live — dashboard of interviews happening right now ────────────────
// Polls GET /job-posting/live-interviews every 10s. No WebSocket yet; pure polling.

const POLL_INTERVAL_MS = 10_000;

const HEALTH_META = {
  healthy:  { label: "Healthy",  dot: "#10B981", text: "text-emerald-700", bg: "bg-emerald-50" },
  slow:     { label: "Slow",     dot: "#F59E0B", text: "text-amber-700",   bg: "bg-amber-50" },
  stalled:  { label: "Stalled",  dot: "#EF4444", text: "text-red-700",     bg: "bg-red-50" },
  awaiting: { label: "Awaiting", dot: "#7B5AC4", text: "text-vf-primary",  bg: "bg-vf-tint" },
  unknown:  { label: "Unknown",  dot: "#94A3B8", text: "text-slate-600",   bg: "bg-slate-100" },
};

const STATE_META = {
  CREATED:            { label: "Created",           badge: "bg-slate-100 text-slate-600" },
  INVITED:            { label: "Invited",           badge: "bg-vf-tint text-vf-primary" },
  ACCEPTED:           { label: "Accepted",          badge: "bg-sky-50 text-sky-700" },
  IN_PROGRESS:        { label: "In progress",       badge: "bg-amber-50 text-amber-700" },
  RECORDING_UPLOADED: { label: "Recording done",    badge: "bg-emerald-50 text-emerald-700" },
  TRANSCRIBING:       { label: "Transcribing",      badge: "bg-violet-50 text-violet-700" },
  SCORED:             { label: "Scored",            badge: "bg-green-50 text-green-700" },
  FAILED:             { label: "Failed",            badge: "bg-red-50 text-red-700" },
};

const fmtHeartbeat = (sec) => {
  if (sec == null) return "—";
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
};

const initials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

export default function LiveInterviews() {
  const [data, setData] = useState({ sessions: [], aggregate: { live: 0, stalled: 0, completedToday: 0 }, asOf: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState("all"); // all | healthy | slow | stalled | awaiting
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  const fetchLive = async () => {
    try {
      // Tenant is resolved from the JWT on the server; no query param needed.
      const r = await axiosInstance.get("/job-posting/live-interviews");
      setData(r?.data || { sessions: [], aggregate: { live: 0, stalled: 0, completedToday: 0 } });
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load live interviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Live interviews - Ample Hire";
    fetchLive();
  }, []);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    intervalRef.current = setInterval(fetchLive, POLL_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [paused]);

  const visible = useMemo(() => {
    if (filter === "all") return data.sessions;
    return data.sessions.filter((s) => s.health === filter);
  }, [data.sessions, filter]);

  const { live, stalled, completedToday } = data.aggregate || {};

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Live interviews</h1>
          <p className="text-sm text-vf-muted mt-1">
            Candidates currently recording. Updates every {POLL_INTERVAL_MS / 1000}s.
            {data.asOf && <span className="ml-2 text-vf-muted">· as of {new Date(data.asOf).toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPaused((p) => !p)}
            className={`px-3 py-1.5 rounded text-xs font-medium border ${paused ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 text-vf-muted hover:bg-vf-tint"}`}
          >
            {paused ? "▶ Resume" : "⏸ Pause"}
          </button>
          <button onClick={fetchLive} className="px-3 py-1.5 rounded text-xs font-medium border border-slate-200 text-vf-muted hover:bg-vf-tint">
            Refresh now
          </button>
        </div>
      </div>

      {/* Aggregate strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Metric label="Live right now" value={live ?? 0} color="#F59E0B" subtext="Recording or accepted" />
        <Metric label="Stalled" value={stalled ?? 0} color={(stalled ?? 0) > 0 ? "#EF4444" : "#94A3B8"} subtext="No heartbeat > 60s" />
        <Metric label="Completed today" value={completedToday ?? 0} color="#10B981" subtext="Recording uploaded or scored" />
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 mb-4 items-center">
        {["all", "healthy", "slow", "stalled", "awaiting"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${
              filter === f ? "bg-vf-tint border-vf-highlight text-vf-primary" : "border-slate-200 text-vf-muted hover:bg-vf-tint"
            }`}
          >
            {f === "all" ? `All (${data.sessions.length})` : f}
          </button>
        ))}
        {error && <span className="ml-auto text-xs text-red-600">⚠ {error}</span>}
      </div>

      {/* Sessions table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-4"><LoadingState variant="skeleton" shape="row" count={4} /></div>
        ) : visible.length === 0 ? (
          <EmptyState
            title={filter === "all" ? "No interviews in progress" : `No ${filter} sessions`}
            description={
              filter === "all"
                ? "Candidates currently recording will show up here in real time. Polling every 10s."
                : `No sessions in "${filter}" state right now. Try "All" or another filter.`
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-vf-muted uppercase bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2">Candidate</th>
                <th className="py-2">State</th>
                <th className="py-2">Heartbeat</th>
                <th className="py-2">Health</th>
                <th className="py-2">Started</th>
                <th className="py-2 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const health = HEALTH_META[s.health] || HEALTH_META.unknown;
                const state = STATE_META[s.state] || { label: s.state, badge: "bg-slate-100 text-slate-600" };
                return (
                  <tr key={s.interviewId} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-medium">
                          {initials(s.candidateName)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-vf-base">{s.candidateName}</div>
                          <div className="text-[11px] text-vf-muted">Interview #{s.interviewId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${state.badge}`}>{state.label}</span>
                    </td>
                    <td className="py-2 text-xs text-vf-muted">{fmtHeartbeat(s.secondsSinceHeartbeat)}</td>
                    <td className="py-2">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${health.bg} ${health.text}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: health.dot }} /> {health.label}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-vf-muted">{s.startedAt ? new Date(s.startedAt).toLocaleString() : "—"}</td>
                    <td className="py-2 px-4 text-right">
                      <button
                        onClick={() => navigate(`/admin/candidate-report?candidateId=${s.userId}&interviewId=${s.interviewId}`)}
                        className="px-2 py-1 rounded border border-slate-200 text-[11px] text-vf-base hover:bg-vf-tint/30"
                      >
                        Open report
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── small helpers ────────────────────────────────────────────────────────────
const Metric = ({ label, value, color, subtext }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
    <div className="text-[11px] uppercase text-vf-muted font-semibold tracking-wide">{label}</div>
    <div className="text-3xl font-semibold mt-1" style={{ color }}>{value}</div>
    <div className="text-[11px] text-vf-muted mt-0.5">{subtext}</div>
  </div>
);
